import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CourseMap from './CourseMap';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './courses.css';
import axiosInstance from '../axiosConfig';
import LoadingSpinner from './LoadingSpinner';
import SubmitUpdateForm from './submitupdatesmall';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const OWNERSHIP_OPTIONS = [
    { label: 'All Types', value: '' },
    { label: 'Member-Owned', value: 'member-owned' },
    { label: 'Corporate', value: 'corporate' },
    { label: 'Other', value: 'other' },
];

const Courses = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [filteredCities, setFilteredCities] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [sliderRange, setSliderRange] = useState([0, 100000]);
    const [maxInitiationFee, setMaxInitiationFee] = useState(100000);
    const [globalMinFee, setGlobalMinFee] = useState(0);
    const [globalMaxFee, setGlobalMaxFee] = useState(100000);
    const [isFiltersVisible, setIsFiltersVisible] = useState(true);
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);
    const prevSelectedState = useRef(selectedState);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [dataSource, setDataSource] = useState('unknown'); // Track which data source was used
    const [selectedOwnership, setSelectedOwnership] = useState('');
    const [selectedCorporateGroup, setSelectedCorporateGroup] = useState('');
    const [invitationRequired, setInvitationRequired] = useState(null);
    const [showUpdateForm, setShowUpdateForm] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedCourseName, setSelectedCourseName] = useState('');

    // Derive corporate groups from loaded data (matching mobile SearchScreen.js)
    const corporateGroups = useMemo(() => {
        const groups = new Set();
        courses.forEach(c => {
            if (c.ownership && c.ownership !== 'member-owned' && c.ownership !== 'unknown') {
                groups.add(c.ownership);
            }
        });
        return [
            { label: 'All Groups', value: '' },
            ...Array.from(groups).sort().map(g => ({ label: g, value: g })),
        ];
    }, [courses]);

    // Clear corporate group sub-filter when ownership changes away from 'corporate'
    useEffect(() => {
        if (selectedOwnership !== 'corporate') {
            setSelectedCorporateGroup('');
        }
    }, [selectedOwnership]);

    useEffect(() => {
        // Add resize event listener to detect mobile/desktop view changes
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);

        document.body.style.zoom = "100%";
        return () => {
            document.body.style.zoom = "";
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log('Attempting to fetch from cache endpoint...');

                // PRIMARY: Try the cached endpoint first
                const response = await axiosInstance.get('/clubs-for-courses2/'); // FIXED: Use correct endpoint

                // Extract clubs data from the response
                const clubsData = response.data.clubs || response.data;

                // Transform club data to match the expected course structure
                const coursesData = clubsData.map(club => ({
                    // Use club_id as the primary identifier for both course and club references
                    id: club.club_id || club.id,
                    club_id: club.club_id || club.id,
                    slug: club.slug, // ADDED: Include slug for URL routing

                    // Course information
                    golf_course: club.name || club.golf_course,
                    city: club.city,
                    state: club.state,
                    address: club.address,
                    phone_number: club.phone_number,
                    website: club.website,

                    // Coordinates - handle both formats
                    latitude: club.coordinates?.lat || club.latitude,
                    longitude: club.coordinates?.lng || club.longitude,

                    // Course details
                    number_of_holes: club.number_of_holes || 18,
                    yards: club.yards,
                    par: club.par,
                    access_type: club.access_type || 'private',

                    // Financial data
                    initiation_fee: club.initiation_fee,
                    annual_dues: club.annual_dues,
                    monthly_dues: club.monthly_dues,
                    source: club.source,
                    source_url: club.source_url,

                    // Ranking and other data
                    usa_golf_magazine_top_100: club.usa_golf_magazine_top_100,
                    architects: club.architects,
                    year_founded: club.year_founded,

                    // Metadata
                    last_updated: club.last_updated,
                    cache_hit: club.cache_hit || true,

                    // Club structure data for filtering
                    ownership: club.ownership || 'unknown',
                    invitation_required: club.invitation_required || false,
                }));

                setCourses(coursesData);
                setDataSource('cache');

                // Calculate fee ranges for slider - handle both numeric and string values
                const initiationFees = coursesData
                    .map(c => {
                        const fee = c.initiation_fee;
                        if (fee === 'N/A' || fee === null || fee === undefined) return null;

                        // Handle numeric values
                        if (typeof fee === 'number') return fee;

                        // Handle string values that might be numeric
                        if (typeof fee === 'string') {
                            const parsed = parseFloat(fee.replace(/[^0-9.-]/g, ''));
                            return isNaN(parsed) ? null : parsed;
                        }

                        return null;
                    })
                    .filter(f => f !== null && f >= 0); // FIXED: Include 0 values

                if (initiationFees.length > 0) {
                    const globalMin = Math.min(...initiationFees);
                    const globalMax = Math.max(...initiationFees);

                    setGlobalMinFee(globalMin);
                    setGlobalMaxFee(globalMax);
                    setSliderRange([globalMin, globalMax]);
                    setMaxInitiationFee(globalMax);
                } else {
                    // Fallback values if no numeric fees found
                    setGlobalMinFee(0);
                    setGlobalMaxFee(100000);
                    setSliderRange([0, 100000]);
                    setMaxInitiationFee(100000);
                }

                setLoading(false);
                console.log(`✅ Cache endpoint success: Loaded ${coursesData.length} clubs from cache`);

            } catch (cacheError) {
                console.warn('❌ Cache endpoint failed, falling back to legacy APIs:', cacheError.message);

                try {
                    // FALLBACK: Use the original multiple API approach
                    console.log('Attempting fallback to legacy endpoints...');

                    const [coursesRes, feesRes] = await Promise.all([
                        axiosInstance.get('/courses/'),
                        axiosInstance.get('/golf-course-fees/')
                    ]);

                    const mergedData = coursesRes.data.map(course => {
                        const fees = feesRes.data.find(f => f.id === course.id);
                        return {
                            ...course,
                            club_id: course.id, // Use course id as club_id for consistency
                            slug: null, // ADDED: No slug available in legacy data
                            initiation_fee: fees?.initiation_fee || 'N/A',
                            source: fees?.source || 'N/A',
                            year_founded: fees?.year_founded || 'N/A',
                            cache_hit: false,
                            ownership: 'unknown',
                            invitation_required: false,
                        };
                    });

                    setCourses(mergedData);
                    setDataSource('legacy');

                    // Fetch rankings data separately (as in original)
                    try {
                        const rankingRes = await axiosInstance.get('/course-rankings/');
                        const rankingData = rankingRes.data;

                        setCourses(prev => prev.map(course => ({
                            ...course,
                            usa_golf_magazine_top_100: rankingData.find(r => r.id === course.id)?.usa_golf_magazine_top_100 || null
                        })));
                    } catch (rankingError) {
                        console.warn('Rankings data failed to load:', rankingError);
                    }

                    // Calculate fee ranges
                    const initiationFees = mergedData
                        .map(c => c.initiation_fee)
                        .filter(f => f !== 'N/A' && typeof f === 'number');

                    if (initiationFees.length > 0) {
                        const globalMin = Math.min(...initiationFees);
                        const globalMax = Math.max(...initiationFees);

                        setGlobalMinFee(globalMin);
                        setGlobalMaxFee(globalMax);
                        setSliderRange([globalMin, globalMax]);
                        setMaxInitiationFee(globalMax);
                    }

                    console.log(`✅ Legacy endpoints success: Loaded ${mergedData.length} courses`);

                } catch (fallbackError) {
                    console.error('❌ Both cache and legacy endpoints failed:', fallbackError);
                    // You could show an error message to the user here
                }

                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const filteredFees = courses
            .filter(course => {
                const stateMatch = selectedState ?
                    course.state.toUpperCase() === selectedState : true;
                const cityMatch = selectedCity ?
                    course.city === selectedCity : true;
                return stateMatch && cityMatch;
            })
            .map(c => {
                const fee = c.initiation_fee;
                if (fee === 'N/A' || fee === null || fee === undefined) return null;
                if (typeof fee === 'number') return fee;
                if (typeof fee === 'string') {
                    const parsed = parseFloat(fee.replace(/[^0-9.-]/g, ''));
                    return isNaN(parsed) ? null : parsed;
                }
                return null;
            })
            .filter(f => f !== null);

        const currentMax = filteredFees.length ?
            Math.max(...filteredFees) : globalMaxFee;

        setMaxInitiationFee(currentMax);

        // Handle state transitions for slider range
        if (prevSelectedState.current !== selectedState) {
            if (selectedState === '') {
                // Reset when switching to 'All States'
                setSliderRange([globalMinFee, globalMaxFee]);
            } else {
                // When switching to a specific state, cap the upper range
                const newUpper = Math.min(sliderRange[1], currentMax);
                setSliderRange([sliderRange[0], newUpper]);
            }
        }

        prevSelectedState.current = selectedState;
    }, [selectedState, selectedCity, courses, globalMinFee, globalMaxFee]);

    useEffect(() => {
        if (selectedState) {
            const cities = [...new Set(courses
                .filter(c => c.state.toUpperCase() === selectedState)
                .map(c => c.city.charAt(0).toUpperCase() + c.city.slice(1).toLowerCase())
            )].sort();

            setFilteredCities(cities);
            if (!cities.includes(selectedCity)) setSelectedCity('');
        } else {
            setFilteredCities([]);
            setSelectedCity('');
        }
    }, [selectedState, courses, selectedCity]);

    const handleSliderChange = (values) => {
        setSliderRange(values);
    };

    const handleCourseClick = (course) => {
        setSelectedCourse(course);
    };

    const handleStateChange = (newState) => {
        if (selectedCity) {
            alert('Please select "All Cities" before changing state');
            return;
        }
        setSelectedState(newState);
    };

    const toggleFilterOptions = () => {
        setIsFilterExpanded(!isFilterExpanded);
    };

    const handleOpenUpdateForm = (course, e) => {
        e.stopPropagation();
        setSelectedCourseId(course.club_id);
        setSelectedCourseName(course.golf_course || 'Unknown Course');
        setShowUpdateForm(true);
    };

    const handleCloseUpdateForm = () => {
        setShowUpdateForm(false);
        setSelectedCourseId(null);
        setSelectedCourseName('');
    };

    // Helper function to normalize initiation fee for filtering
    const normalizeInitiationFee = (fee) => {
        if (fee === 'N/A' || fee === null || fee === undefined) return null;
        if (typeof fee === 'number') return fee;
        if (typeof fee === 'string') {
            const parsed = parseFloat(fee.replace(/[^0-9.-]/g, ''));
            return isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    const filteredCourses = courses
        .filter(course => {
            const searchMatch = course.golf_course.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.state.toLowerCase().includes(searchTerm.toLowerCase());

            const stateMatch = selectedState ?
                course.state.toUpperCase() === selectedState : true;
            const cityMatch = selectedCity ?
                course.city.toLowerCase() === selectedCity.toLowerCase() : true;

            // Handle initiation fee filtering with normalization
            // Treat null/N/A fees as 0 so they respect the slider range
            const normalizedFee = normalizeInitiationFee(course.initiation_fee) ?? 0;
            const feeMatch = normalizedFee >= sliderRange[0] && normalizedFee <= sliderRange[1];

            // Ownership filter (matching mobile SearchScreen.js lines 327-338)
            let ownershipMatch = true;
            if (selectedOwnership === 'member-owned') {
                ownershipMatch = course.ownership === 'member-owned';
            } else if (selectedOwnership === 'corporate') {
                ownershipMatch = course.ownership !== 'member-owned' && course.ownership !== 'unknown';
                if (selectedCorporateGroup) {
                    ownershipMatch = ownershipMatch && course.ownership === selectedCorporateGroup;
                }
            } else if (selectedOwnership === 'other') {
                ownershipMatch = course.ownership === 'unknown';
            }

            // Invitation required filter
            const invitationMatch = invitationRequired === null ||
                course.invitation_required === invitationRequired;

            return searchMatch && stateMatch && cityMatch && feeMatch && ownershipMatch && invitationMatch;
        })
        .reduce((unique, current) => {
            const exists = unique.find(c =>
                c.golf_course === current.golf_course &&
                c.state === current.state &&
                c.city === current.city
            );
            return exists ?
                (current.yards > exists.yards ?
                    [...unique.filter(u => u !== exists), current] : unique)
                : [...unique, current];
        }, []);

    // Format initiation fee for display
    const formatInitiationFee = (fee) => {
        if (fee === 'N/A' || fee === null || fee === undefined) {
            return '$0';
        }
        if (typeof fee === 'number') {
            return `$${fee.toLocaleString()}`;
        }
        if (typeof fee === 'string') {
            // Try to parse string values
            const parsed = parseFloat(fee.replace(/[^0-9.-]/g, ''));
            if (!isNaN(parsed)) {
                return `$${parsed.toLocaleString()}`;
            }
        }
        return '$0';
    };

    // UPDATED: Helper function to generate club URL
    const getClubUrl = (course) => {
        // Use slug if available, fallback to club_id
        if (course.slug) {
            return `/club/${course.slug}`;
        } else if (course.club_id) {
            return `/club/${course.club_id}`; // This will redirect to slug URL
        } else {
            return `/course/${course.id}`; // Legacy fallback
        }
    };

    // Render desktop filter controls - original layout
    const renderDesktopFilters = () => (
        <div className="desktop-filters">
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by course, city, or state..."
            />

            <select
                value={selectedState}
                onChange={(e) => handleStateChange(e.target.value)}
            >
                <option value="">All States</option>
                {[...new Set(courses.map((course) => course.state.toUpperCase()))]
                    .sort()
                    .map((state) => (
                        <option key={state} value={state}>
                            {state}
                        </option>
                    ))}
            </select>

            <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                disabled={!selectedState}
            >
                <option value="">All Cities</option>
                {filteredCities.sort().map((city) => (
                    <option key={city} value={city}>
                        {city}
                    </option>
                ))}
            </select>

            <select
                value={selectedOwnership}
                onChange={(e) => setSelectedOwnership(e.target.value)}
            >
                {OWNERSHIP_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            {selectedOwnership === 'corporate' && (
                <select
                    value={selectedCorporateGroup}
                    onChange={(e) => setSelectedCorporateGroup(e.target.value)}
                >
                    {corporateGroups.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            )}

            <select
                value={invitationRequired === null ? '' : invitationRequired.toString()}
                onChange={(e) => {
                    const val = e.target.value;
                    setInvitationRequired(val === '' ? null : val === 'true');
                }}
            >
                <option value="">Invite Only: Any</option>
                <option value="true">Invite Only: Yes</option>
                <option value="false">Invite Only: No</option>
            </select>

            <div className="slider-container">
                <label>
                    Initiation Fee Range: $
                    {sliderRange[0].toLocaleString()} - $
                    {sliderRange[1].toLocaleString()}
                </label>
                <div className="slider-padding">
                    <Slider
                        range
                        min={0}
                        max={maxInitiationFee}
                        value={sliderRange}
                        onChange={handleSliderChange}
                        step={5000}
                    />
                </div>
            </div>
        </div>
    );

    // Render mobile filter controls - compact version
    const renderMobileFilters = () => (
        <>
            {/* Search input - always visible */}
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search course, city, state..."
            />

            {/* Filter header - clickable to expand/collapse filters */}
            <div className="filter-header" onClick={toggleFilterOptions}>
                <h3>Advanced Filters</h3>
                <span>{isFilterExpanded ? '▲' : '▼'}</span>
            </div>

            {/* Collapsible filter options */}
            <div className={`filter-options ${isFilterExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="filter-selects">
                    <select
                        value={selectedState}
                        onChange={(e) => handleStateChange(e.target.value)}
                    >
                        <option value="">All States</option>
                        {[...new Set(courses.map((course) => course.state.toUpperCase()))]
                            .sort()
                            .map((state) => (
                                <option key={state} value={state}>
                                    {state}
                                </option>
                            ))}
                    </select>

                    <select
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        disabled={!selectedState}
                    >
                        <option value="">All Cities</option>
                        {filteredCities.sort().map((city) => (
                            <option key={city} value={city}>
                                {city}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="slider-container">
                    <label>
                        <span>Fee Range:</span>
                        <span>${sliderRange[0].toLocaleString()} - ${sliderRange[1].toLocaleString()}</span>
                    </label>
                    <div className="slider-padding">
                        <Slider
                            range
                            min={0}
                            max={maxInitiationFee}
                            value={sliderRange}
                            onChange={handleSliderChange}
                            step={5000}
                        />
                    </div>
                </div>

                <div className="filter-selects" style={{ marginTop: '8px' }}>
                    <select
                        value={selectedOwnership}
                        onChange={(e) => setSelectedOwnership(e.target.value)}
                    >
                        {OWNERSHIP_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    {selectedOwnership === 'corporate' ? (
                        <select
                            value={selectedCorporateGroup}
                            onChange={(e) => setSelectedCorporateGroup(e.target.value)}
                        >
                            {corporateGroups.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    ) : (
                        <select
                            value={invitationRequired === null ? '' : invitationRequired.toString()}
                            onChange={(e) => {
                                const val = e.target.value;
                                setInvitationRequired(val === '' ? null : val === 'true');
                            }}
                        >
                            <option value="">Invite Only: Any</option>
                            <option value="true">Invite Only: Yes</option>
                            <option value="false">Invite Only: No</option>
                        </select>
                    )}
                </div>

                {selectedOwnership === 'corporate' && (
                    <div className="filter-selects" style={{ marginTop: '8px' }}>
                        <select
                            value={invitationRequired === null ? '' : invitationRequired.toString()}
                            onChange={(e) => {
                                const val = e.target.value;
                                setInvitationRequired(val === '' ? null : val === 'true');
                            }}
                        >
                            <option value="">Invite Only: Any</option>
                            <option value="true">Invite Only: Yes</option>
                            <option value="false">Invite Only: No</option>
                        </select>
                    </div>
                )}
            </div>
        </>
    );

    return (
        <>
            {loading ? (
                <LoadingSpinner />
            ) : (
                <div className="courses-container">
                    {/* Toggle filters button */}
                    <button
                        className="toggle-filters-button"
                        onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    >
                        {isFiltersVisible ? "Hide Filters" : "Show Filters"}
                    </button>

                    {/* Sidebar for filters and course list */}
                    <div className="sidebar">
                        {isFiltersVisible && (
                            <div className="filter-container">
                                {/* Render different filters based on screen size */}
                                {isMobile ? renderMobileFilters() : renderDesktopFilters()}
                            </div>
                        )}

                        {/* Course List */}
                        <div className="course-list">
                            {filteredCourses.length > 0 ? (
                                filteredCourses.map((course) => (
                                    <div
                                        key={course.club_id}
                                        className="course-card"
                                        onClick={() => handleCourseClick(course)}
                                    >
                                        <h2 className="course-name">
                                            {course.golf_course || 'Course Name Unknown'}
                                        </h2>
                                        <Link
                                            to={getClubUrl(course)}
                                            className="view-details-btn"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="14" height="14">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                            </svg>
                                        </Link>
                                        <div className="card-meta">
                                            <span className="location">
                                                <span className="city-name2">{course.city},</span>{' '}
                                                <span className="state-abbreviation">{course.state}</span>
                                            </span>
                                            {course.year_founded && (
                                                <>
                                                    <span className="meta-sep">•</span>
                                                    <span className="year-founded">Est. {course.year_founded}</span>
                                                </>
                                            )}
                                            <span className="meta-sep">•</span>
                                            <span className="fee-inline">{formatInitiationFee(course.initiation_fee)}</span>
                                        </div>
                                        {(course.usa_golf_magazine_top_100 || course.invitation_required || course.source) && (
                                            <div className="card-bottom-row">
                                                {course.usa_golf_magazine_top_100 && (
                                                    <span className="ranking-compact">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="ranking-icon">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0" />
                                                        </svg>
                                                        Top 100: #{course.usa_golf_magazine_top_100}
                                                    </span>
                                                )}
                                                {course.invitation_required && (
                                                    <span className="invite-badge">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="invite-icon">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 0 0 1.183 1.981l6.478 3.488m8.839 2.51-4.66-2.51m0 0-1.023-.55a2.25 2.25 0 0 0-2.134 0l-1.022.55m0 0-4.661 2.51m16.5 1.615a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V8.844a2.25 2.25 0 0 1 1.183-1.981l7.5-4.039a2.25 2.25 0 0 1 2.134 0l7.5 4.039a2.25 2.25 0 0 1 1.183 1.98V19.5Z" />
                                                        </svg>
                                                        Invite Only
                                                    </span>
                                                )}
                                                {course.source && (
                                                    <span className="source-compact" title={course.source}>
                                                        {course.source.length > 50 ? course.source.substring(0, 50) + '...' : course.source}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <button
                                            className="card-edit-btn"
                                            onClick={(e) => handleOpenUpdateForm(course, e)}
                                            title="Submit update"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p>No courses found.</p>
                            )}
                        </div>
                    </div>

                    <div className="map-container">
                        <CourseMap
                            courses={filteredCourses}
                            selectedCourse={selectedCourse}
                            selectedState={selectedState}
                            selectedCity={selectedCity}
                        />
                    </div>
                </div>
            )}
            {/* Update Form Modal */}
            {showUpdateForm && selectedCourseId && (
                <SubmitUpdateForm
                    courseId={selectedCourseId}
                    courseName={selectedCourseName}
                    onClose={handleCloseUpdateForm}
                />
            )}
        </>
    );
};

export default Courses;